import styled from "@emotion/styled";
import { format } from "date-fns";
import * as React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Dispatch } from "redux";

import {
  Button,
  Checkbox,
  DesignToken,
  FormField,
  Heading,
  Input,
  SpacingToken,
  Tag,
  Text,
} from "@livechat/design-system-react-components";

import { ExternalLink } from "components/external-link";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { ActionLink } from "components/links/ActionLink";
import { SettingsTable, SettingsTableColumn, SettingsTableRow } from "components/settings-table";
import { DateFormat } from "constants/date";
import { HelpCenter } from "constants/help-center";
import Role from "constants/role";
import { EventPlace, pushDataLayerEvent, trackEngagement } from "helpers/analytics";
import { getTagAuthorName, getTagAuthorType, tagWasUpdated } from "helpers/tags";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { IStoreState } from "interfaces/storeState";
import { IAgent, selectAgentsAlphabetically, selectLoggedInAgentPermission } from "store/entities/agents";
import { ITag, selectCanManageTags, selectSettingsTags } from "store/entities/tags";
import {
  ALL_TEAMS_ID,
  ITeam,
  TeamActions,
  selectCurrentAgentTeams,
  selectIsTagReminderEnabled,
} from "store/entities/teams";
import { SettingsViewActions, getSelectedTeamId } from "store/views/settings";
import { withVar } from "themes/helpers";
import { addCommas, plural } from "utils/string";

import TeamSelection from "../TeamSelection";
import SettingsViewContainer from "../ViewContainer";
import { ContentWrapper, InlineForm, PageWrapper } from "../styled";

interface IStateProps {
  agents: IAgent[];
  teams: ITeam[];
  tags: ITag[];
  canManageTags: boolean;
  role: Role;
  selectedTeamId: string;
  tagReminder: boolean;
}

interface IDispatchProps {
  setTeamId(teamId: string): void;
  onAddTag(teamId: string, name: string): void;
  onUpdateTag(id: string, name: string): void;
  onDeleteTag(id: string): void;
  onTagReminderToggle(tagReminder: boolean, teamID: string | string[]): void;
}

interface IProps extends IStateProps, IDispatchProps, IWithBreakpointsProps {}

interface IState {
  newTagName: string;
  updateTagName: string;
  updateTagId: string | null;
  deleteTagId: string | null;
  addValidationError: string | null;
  updateValidationError: string | null;
}

enum TagValidationResult {
  Ok,
  NotEmpty,
  TooLong,
  NotUnique,
}

function tagNameIsValid(tagName: string, tags: ITag[], teamId: string): TagValidationResult {
  const trimmedTag = tagName.trim();

  if (tags.filter(tag => (tag.teamId ?? "0") === teamId).some(tag => tag.name === trimmedTag)) {
    return TagValidationResult.NotUnique;
  }

  if (trimmedTag.length === 0) {
    return TagValidationResult.NotEmpty;
  }
  if (trimmedTag.length >= 70) {
    return TagValidationResult.TooLong;
  }

  return TagValidationResult.Ok;
}

function validationError(errorType: TagValidationResult): string | null {
  switch (errorType) {
    case TagValidationResult.TooLong:
      return "Tag label is too long (70 characters max)";
    case TagValidationResult.NotEmpty:
      return "Tag label cannot be empty.";
    case TagValidationResult.NotUnique:
      return "Tag with this name already exists.";
    default:
      return null;
  }
}

class Tags extends React.PureComponent<IProps, IState> {
  state: IState = {
    newTagName: "",
    updateTagName: "",
    updateTagId: null,
    deleteTagId: null,
    addValidationError: null,
    updateValidationError: null,
  };

  updateInputRef: React.RefObject<HTMLInputElement> = React.createRef();

  private validateTagName = (tagName: string): TagValidationResult => {
    return tagNameIsValid(tagName, this.props.tags, this.props.selectedTeamId);
  };

  componentDidMount(): void {
    trackEngagement("Settings: Tags section opened", EventPlace.Settings);
  }

  handleTeamSelected = (selectedTeamId: string): void => {
    this.props.setTeamId(selectedTeamId);
  };

  handleNewTagInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      newTagName: e.currentTarget.value,
      addValidationError: validationError(this.validateTagName(e.currentTarget.value)),
    });
  };

  handleUpdateTagInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      updateTagName: e.currentTarget.value,
      updateValidationError: validationError(this.validateTagName(e.currentTarget.value)),
    });
  };

  handleAddButtonClick = (): void => {
    trackEngagement("Tags: Add Tag clicked", EventPlace.Settings);
    pushDataLayerEvent("Tags: Add tag clicked");
  };

  handleAddTag = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const newTagName = this.state.newTagName.trim();
    const tagValidation = this.validateTagName(newTagName);

    if (tagValidation !== TagValidationResult.Ok) {
      return;
    }

    this.props.onAddTag(this.props.selectedTeamId, newTagName);
    this.setState({ newTagName: "" });
  };

  handleUpdateTag = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const { updateTagId } = this.state;
    const updateTagName = this.state.updateTagName.trim();
    const tagValidation = this.validateTagName(updateTagName);
    if (tagValidation !== TagValidationResult.Ok) {
      return;
    }

    if (!updateTagId) {
      return;
    }

    this.props.onUpdateTag(updateTagId, updateTagName);
    this.handleNormalMode();
  };

  handleDeleteTag = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const { deleteTagId } = this.state;
    if (!deleteTagId) {
      return;
    }

    this.props.onDeleteTag(deleteTagId);
    this.handleNormalMode();
  };

  handleNormalMode = (): void => {
    this.setState({
      updateTagId: null,
      deleteTagId: null,
      updateValidationError: null,
    });
  };

  handleUpdateMode = (tagId: string, tagName: string): void => {
    this.setState(
      {
        updateTagName: tagName,
        updateTagId: tagId,
        updateValidationError: null,
        deleteTagId: null,
      },
      () => {
        const input = this.updateInputRef.current;
        if (input) {
          input.select();
        }
      }
    );
  };

  handleDeleteMode = (tagId: string): void => {
    this.setState({
      deleteTagId: tagId,
      updateValidationError: null,
      updateTagId: null,
    });
  };

  handleTagReminderToggle = (): void => {
    const { onTagReminderToggle, tagReminder, selectedTeamId, teams } = this.props;
    if (selectedTeamId === ALL_TEAMS_ID) {
      const allTeamIDs = teams.map(t => t.id);
      onTagReminderToggle(!tagReminder, allTeamIDs);
    } else {
      onTagReminderToggle(!tagReminder, selectedTeamId);
    }
  };

  handleAddTagInputBlur = (): void => {
    const validationError = this.validateTagName(this.state.newTagName);
    if (validationError === TagValidationResult.NotEmpty) {
      this.setState({ addValidationError: null });
    }
  };

  render(): React.ReactNode {
    const { agents, teams, tags, selectedTeamId, canManageTags, isMobile, tagReminder, role } = this.props;
    const { newTagName, updateTagId, updateTagName, deleteTagId, addValidationError, updateValidationError } =
      this.state;

    return (
      <SettingsViewContainer title="Tags">
        <PageWrapper>
          <TeamSelection
            teams={teams}
            selectedTeamId={selectedTeamId}
            includeAll
            onTeamSelected={this.handleTeamSelected}
          />
          <ContentWrapper>
            <Block marginBottom={withVar(SpacingToken.Spacing4)}>
              <Text>
                Use tags to categorize your tickets. Tags are great for characterizing a case, filtering tickets, and
                improving the sense of ticket ownership.{" "}
                <ExternalLink
                  target="_blank"
                  href={HelpCenter.WHAT_ARE_TAGS_AND_HOW_TO_USE_THEM}
                  onClick={() => trackEngagement("Tags: Learn more clicked", EventPlace.Settings)}
                >
                  Learn more
                </ExternalLink>
              </Text>
            </Block>
            {canManageTags && (
              <>
                {role === Role.Owner && (
                  <Block marginBottom={withVar(SpacingToken.Spacing8)}>
                    <Checkbox
                      description="A reminder to tag tickets will be displayed after an agent sends a message."
                      checked={tagReminder}
                      onChange={this.handleTagReminderToggle}
                      data-testid={`tag-reminder-checkbox-${selectedTeamId}`}
                    >
                      Remind to tag tickets
                    </Checkbox>
                  </Block>
                )}
                <Block marginBottom="2em">
                  <Heading size="xs" as="h2">
                    Tags available for: {teams.find(team => team.id === selectedTeamId)?.name || "All teams"}
                  </Heading>
                  <Text css={{ marginBottom: withVar(SpacingToken.Spacing3) }}>
                    Enter the names of your tags to enrich the ticket with information.
                  </Text>
                  <InlineForm onSubmit={this.handleAddTag}>
                    <Flex gap={withVar(SpacingToken.Spacing2)}>
                      <InputField error={addValidationError ?? undefined}>
                        <TagInput
                          id="add-tag-name"
                          placeholder="Enter tag name…"
                          inputSize="compact"
                          value={newTagName}
                          onChange={this.handleNewTagInputChange}
                          onBlur={this.handleAddTagInputBlur}
                          error={!!addValidationError}
                        />
                      </InputField>
                      <Button
                        size="compact"
                        kind="primary"
                        type="submit"
                        disabled={this.validateTagName(newTagName) !== TagValidationResult.Ok}
                        onClick={this.handleAddButtonClick}
                      >
                        Add a tag
                      </Button>
                    </Flex>
                  </InlineForm>
                </Block>
              </>
            )}
            {tags.length > 0 && (
              <SettingsTable
                columns={isMobile ? 2 : 3}
                headers={isMobile ? ["Tag", "Info / Actions"] : ["Tag", "Uses", "Info / Actions"]}
                columnSizes={`repeat(${isMobile ? 2 : 3}, 1fr)`}
                marginTop="0px"
              >
                {tags.map(tag => {
                  const updateMode = updateTagId === tag.id;
                  const deleteMode = deleteTagId === tag.id;
                  const normalMode = !updateMode && !deleteMode;
                  const tagUpdated = tagWasUpdated(tag);
                  const tagCreatedAutomatically = getTagAuthorType(tag) === "system";

                  return (
                    <SettingsTableRow key={tag.id} className="tag-row">
                      <SettingsTableColumn noHeader>
                        {updateMode && (
                          <Flex height="100%" alignItems="center">
                            <InlineForm onSubmit={this.handleUpdateTag}>
                              <InputField error={updateValidationError ?? undefined}>
                                <TagInput
                                  id="update-tag-name"
                                  value={updateTagName}
                                  onChange={this.handleUpdateTagInputChange}
                                  ref={this.updateInputRef}
                                  error={!!updateValidationError}
                                />
                              </InputField>
                            </InlineForm>
                          </Flex>
                        )}
                        {!updateMode && (
                          <Flex alignItems="center" height="100%">
                            <TagBadge>{tag.name}</TagBadge>
                          </Flex>
                        )}
                      </SettingsTableColumn>
                      {!isMobile && (
                        <SettingsTableColumn noHeader>
                          <Flex alignItems="center" height="100%">
                            {tag.count === 0 && <>0 tickets</>}
                            {tag.count > 0 && (
                              <Link
                                to={`/tickets/all?tag=${tag.id}`}
                                onClick={() =>
                                  trackEngagement("Show tickets with tag", EventPlace.Settings, { tagId: tag.id })
                                }
                              >
                                {`${addCommas(tag.count)} ${plural(tag.count, "ticket", "tickets")}`}
                              </Link>
                            )}
                          </Flex>
                        </SettingsTableColumn>
                      )}
                      <SettingsTableColumn noHeader>
                        {tagCreatedAutomatically && !deleteMode && (
                          <Text size="sm" customColor={withVar(DesignToken.ContentBasicDisabled)}>
                            {tagUpdated ? "Updated" : "Added"} automatically on{" "}
                            {format(tag.updatedAt, DateFormat.DateWithoutDayOfTheWeek)}
                          </Text>
                        )}
                        {!tagCreatedAutomatically && !deleteMode && (
                          <Text>
                            {getTagAuthorName(tag, agents)}
                            <br />
                            <Text size="sm" customColor={withVar(DesignToken.ContentBasicDisabled)}>
                              {tagUpdated ? "updated" : "added"} this tag on{" "}
                              {format(tag.updatedAt, DateFormat.DateWithoutDayOfTheWeek)}
                            </Text>
                          </Text>
                        )}
                        {deleteMode && (
                          <Text css={{ marginBottom: withVar(SpacingToken.Spacing4) }}>
                            Are you sure you want to delete this tag?
                          </Text>
                        )}
                        {updateMode && (
                          <InlineForm onSubmit={this.handleUpdateTag}>
                            <ButtonGroup>
                              <Button
                                size="compact"
                                type="submit"
                                kind="primary"
                                disabled={this.validateTagName(updateTagName) !== TagValidationResult.Ok}
                              >
                                Save
                              </Button>
                              <Button size="compact" type="submit" onClick={this.handleNormalMode} kind="secondary">
                                Cancel
                              </Button>
                            </ButtonGroup>
                          </InlineForm>
                        )}
                        {deleteMode && (
                          <InlineForm onSubmit={this.handleDeleteTag}>
                            <ButtonGroup>
                              <Button size="compact" kind="destructive" type="submit">
                                Delete
                              </Button>
                              <Button size="compact" kind="secondary" onClick={this.handleNormalMode}>
                                Cancel
                              </Button>
                            </ButtonGroup>
                          </InlineForm>
                        )}
                        {normalMode && canManageTags && (
                          <ButtonGroup>
                            <Button
                              kind="link-light"
                              onClick={() => this.handleUpdateMode(tag.id, tag.name)}
                              data-testid={`edit-tag-${tag.name}`}
                            >
                              Edit
                            </Button>
                            <ActionLink
                              warning
                              onClick={() => this.handleDeleteMode(tag.id)}
                              data-testid={`delete-tag-${tag.name}`}
                            >
                              Delete
                            </ActionLink>
                          </ButtonGroup>
                        )}
                      </SettingsTableColumn>
                    </SettingsTableRow>
                  );
                })}
              </SettingsTable>
            )}
          </ContentWrapper>
        </PageWrapper>
      </SettingsViewContainer>
    );
  }
}

const mapStateToProps = (state: IStoreState): IStateProps => ({
  agents: selectAgentsAlphabetically(state),
  teams: selectCurrentAgentTeams(state),
  tags: selectSettingsTags(state),
  canManageTags: selectCanManageTags(state),
  // @ts-expect-error TS(2342)
  role: selectLoggedInAgentPermission(state),
  // @ts-expect-error TS(2342)
  selectedTeamId: getSelectedTeamId(state),
  tagReminder: selectIsTagReminderEnabled(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  // @ts-expect-error TS(2345)
  setTeamId: (teamId: string) => dispatch(SettingsViewActions.setTeamId({ teamId })),
  // @ts-expect-error TS(2345)
  onAddTag: (teamId: string, name: string) => dispatch(SettingsViewActions.addTag({ teamId, name })),
  // @ts-expect-error TS(2345)
  onUpdateTag: (id: string, name: string) => dispatch(SettingsViewActions.updateTag({ id, name })),
  // @ts-expect-error TS(2345)
  onDeleteTag: (id: string) => dispatch(SettingsViewActions.deleteTag({ id })),
  onTagReminderToggle: (tagReminder: boolean, teamID: string | string[]) =>
    // @ts-expect-error TS(2345)
    dispatch(TeamActions.updateTagReminderToggle({ tagReminder, teamID })),
});

const InputField = styled(FormField)`
  margin-bottom: 0;
`;

const TagInput = styled(Input)`
  width: 250px;
  max-width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(${SpacingToken.Spacing2});
  justify-items: flex-start;
  align-items: center;
  margin-top: var(${SpacingToken.Spacing2});
`;

const TagBadge = styled(Tag)`
  max-width: 300px;
`;

export default withBreakpoints(connect(mapStateToProps, mapDispatchToProps)(Tags));
